/**
 * audioTrace — the shared, module-level fire-trace ring for the Sound Debug
 * kit (2026-07-10, "spatial sound still isn't working — give me a debug
 * setup").
 *
 * AudioProbe intercepts the ENGINE's fire(), which only ever sees the
 * already-resolved {pan, gain} — a bypassed spatialization is
 * indistinguishable from a legit zero-distance hit. This ring records at the
 * DECISION point instead: every AudioSpatialSystem.fire() (and every raw
 * engine bypass) logs WHICH PATH the cue took, with the emitter/listener
 * positions that drove the math. "Why was that full volume?" becomes one
 * table read.
 *
 * Module-level singleton on purpose: GameAudioSystem owns the gameplay engine
 * and spatial path, including requests selected by domain binders such as
 * RaceAudioSystem. The debug window wants one pane of glass across normal
 * gameplay plus any explicit isolated debug/test path.
 *
 * Consumers: 🔊 GmSoundDebugWindow (poll peekAudioTrace) · SoundDebugRenderer
 * (drainNewAudioTraces → emitter rings) · headless QA via
 * `__helixDebug.audioTrace()`.
 */

export type AudioFirePath =
  /** Spatial cue, emitterId resolved to a live transform — the happy path. */
  | 'emitter-resolved'
  /** Spatial cue, emitterId given but NOT in the ECS registry — err-quiet. */
  | 'emitter-miss'
  /** Spatial cue resolved from explicit world x/y. */
  | 'xy'
  /** Spatial cue fired with NO position info at all — err-quiet (leak fix:
   *  this used to play center/full-volume). */
  | 'spatial-no-position'
  /** Caller supplied explicit pan/gain — trusted verbatim. */
  | 'caller-provided'
  /** Cue not flagged spatial — plays flat by design (UI, personal feedback). */
  | 'non-spatial'
  /** Fired straight at the engine with no spatial system available. */
  | 'raw-bypass';

export interface AudioFireTrace {
  t: number;
  seq: number;
  /** Which engine/spatial lane fired (normal gameplay, legacy race, or test). */
  source: 'race' | 'game' | 'test';
  cueId: string;
  /** The cue def's spatial flag. */
  spatial: boolean;
  path: AudioFirePath;
  emitterId?: string;
  emitterPos?: { x: number; y: number } | null;
  listenerPos?: { x: number; y: number } | null;
  distance?: number | null;
  pan: number;
  gain: number;
  /** Human note for oddball paths (e.g. which payload field was empty). */
  note?: string;
}

const RING_CAP = 128;

const ring: AudioFireTrace[] = [];
let seq = 0;
const counters: Record<AudioFirePath, number> = {
  'emitter-resolved': 0,
  'emitter-miss': 0,
  'xy': 0,
  'spatial-no-position': 0,
  'caller-provided': 0,
  'non-spatial': 0,
  'raw-bypass': 0,
};

export function recordAudioFire(entry: Omit<AudioFireTrace, 'seq' | 't'> & { t?: number }): AudioFireTrace {
  const full: AudioFireTrace = { t: entry.t ?? Date.now(), seq: seq++, ...entry } as AudioFireTrace;
  ring.push(full);
  if (ring.length > RING_CAP) ring.shift();
  counters[full.path] = (counters[full.path] ?? 0) + 1;
  return full;
}

/** Latest N entries, oldest→newest (live debug table). */
export function peekAudioTrace(count = 20): AudioFireTrace[] {
  return ring.slice(-count);
}

/** Entries newer than `sinceSeq` (overlay ring spawner). */
export function audioTracesSince(sinceSeq: number): AudioFireTrace[] {
  return ring.filter((e) => e.seq > sinceSeq);
}

export function audioTraceCounters(): Readonly<Record<AudioFirePath, number>> {
  return { ...counters };
}

export function clearAudioTrace(): void {
  ring.length = 0;
  for (const k of Object.keys(counters) as AudioFirePath[]) counters[k] = 0;
}

// ── Rings overlay toggle (module state, RaceDebugOverlayState pattern) ────

let ringsEnabled = false;
export function isSoundRingsEnabled(): boolean { return ringsEnabled; }
export function setSoundRingsEnabled(v: boolean): void { ringsEnabled = v; }
export function toggleSoundRings(): boolean { ringsEnabled = !ringsEnabled; return ringsEnabled; }
